import React from 'react';
import dayjs from 'dayjs';

import { isString, isUndefined } from 'utils/check';
import { EyeClosedIcon, EyeIcon, LockIcon } from 'ui/icons';
import { LineLoader } from 'ui/LineLoader';
import { ruDateToDayJs } from 'ui/utils';

import clearClasses from './ClearTextField.module.scss';
import filledClasses from './FilledTextField.module.scss';
import outlinedClasses from './OutlinedTextField.module.scss';
import spaceClasses from './SpaceTextField.module.scss';
import { TextFieldMessage } from './TextFieldMessage';
import type { TextFieldProps } from './types';
import { useTextFieldView } from './useTextFieldView';

import IMask from 'imask';

const maskOptionsMap = {
  'ru-date': {
    mask: Date,
    min: new Date(1999, 0, 1),
    max: new Date(2050, 0, 1),
    lazy: false,
  },
  'ru-phone': {
    mask: '+{7}(000)000-00-00',
    lazy: false,
  },
  'code-l6': {
    mask: '000000',
    lazy: false,
    placeholderChar: 'X',
  },
};

const rootClassesMap = {
  filled: filledClasses,
  outlined: outlinedClasses,
  clear: clearClasses,
  spaces: spaceClasses,
};

const TextFieldInternal: React.ForwardRefRenderFunction<
  HTMLInputElement | HTMLTextAreaElement,
  TextFieldProps
> = (props, _ref) => {
  const {
    prefix = '',
    suffix = '',
    variant = 'filled',
    isInvalid,
    required,
    label,
    loading,
    disabled,
    startAdornment,
    endAdornment,
    helper,
    value,
    tag = 'input',
    inputContainerRef,
    nativeTooltip,
    input,
    onChange,
    blurOnChange,
    onBlur: onBlurProp,
    mask,
    type = 'text',
    name,
    rows,
  } = props;

  const {
    rootProps,
    labelProps,
    labelTextProps,
    labelRequiredMarkerProps,
    inputContainerProps,
    inputProps,
    textareaProps,
    inputLockerProps,
    loaderProps,
    startAdornmentProps,
    endAdornmentProps,
    helperTextProps,
    prefixProps,
    suffixProps,
  } = useTextFieldView(props, rootClassesMap[variant]);

  const ref = _ref || React.useRef<HTMLInputElement | null>(null);
  const [internalValue, setInternalValue] = React.useState('');
  const [internalType, setInternalType] = React.useState(type);
  const [, startTransition] = React.useTransition();

  React.useEffect(() => {
    if (!isUndefined(value)) setInternalValue(value);
  }, [value]);

  const noopChange = () => undefined;
  // Последний onChange родителя: обработчик маски создается один раз и иначе видел бы устаревший.
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const _onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    onChange?.(e.target.value);
  };

  React.useEffect(() => {
    if (!mask) return;

    const _ref = ref as any;
    const _mask = IMask(_ref.current, maskOptionsMap[mask]);

    _mask.on('accept', () => {
      const maskedValue = _mask.value;
      setInternalValue(maskedValue);

      if (mask === 'ru-phone') {
        // Телефон: родитель получает текущее значение на каждое изменение (ввод, удаление,
        // выделить+удалить, вставка, автозаполнение), иначе после стирания у него оставался
        // последний полный номер. Полноту номера проверяют формы (11 цифр).
        const digits = maskedValue.replace(/\D/g, '');
        onChangeRef.current?.(digits.length <= 1 ? '' : maskedValue);
        return;
      }
      if (_mask.masked.isComplete) {
        onChangeRef.current?.(maskedValue);
      }
    });

    return () => _mask.destroy();
  }, [mask]);

  const _onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (mask === 'ru-date' && !dayjs(ruDateToDayJs(e.target.value)).isValid()) {
      startTransition(() => {
        setInternalValue('');
      });
    }

    if (mask === 'ru-phone' && e.target.value[e.target.value.length - 1] === '_') {
      startTransition(() => {
        setInternalValue('');
      });
      // Поле очищено - родитель тоже должен видеть пустое значение.
      onChangeRef.current?.('');
    }

    blurOnChange?.(e.target.value);
    onBlurProp?.(e);
  };

  const _endAdornmentOnClick = () => {
    if (type === 'password') {
      if (internalType === 'password') setInternalType('text');
      else setInternalType('password');
    }
  };

  let endAdornmentFinal = endAdornment;

  if (disabled) endAdornmentFinal = <LockIcon />;

  if (type === 'password' && !endAdornment) {
    if (internalType === 'password') endAdornmentFinal = <EyeClosedIcon />;
    if (internalType === 'text') endAdornmentFinal = <EyeIcon />;
  }

  // Маска телефона без введенных цифр («+7(___)___-__-__») выглядит как подсказка: серым,
  // как placeholder в Figma (#636a70); введенные цифры - обычным цветом поля.
  const phoneMaskEmpty = mask === 'ru-phone' && internalValue.replace(/\D/g, '').length <= 1;

  const errorText = typeof isInvalid === 'string' && isInvalid ? isInvalid : '';
  const errorId = React.useId();
  const invalidStyle = isInvalid
    ? {
        border: '1px solid #f06f71',
      }
    : {};

  return (
    <label className={rootProps.className}>
      {label && (
        <p className={labelProps.className}>
          <span className={labelTextProps.className}>{label}</span>
          {required && <span className={labelRequiredMarkerProps.className}>*</span>}
        </p>
      )}
      <div
        className={inputContainerProps.className}
        style={{
          ...inputContainerProps.style,
          ...invalidStyle,
        }}
        onClick={inputContainerProps.onClick}
        ref={inputContainerRef}
        title={nativeTooltip}
      >
        {loading && <LineLoader {...loaderProps} />}
        {startAdornment && (
          <div className={startAdornmentProps.className}>{startAdornment}</div>
        )}
        <div className={inputLockerProps.className}>
          {prefix && <span className={prefixProps.className}>{prefix}</span>}
          {input?.(inputProps.className)}
          {!input && tag === 'input' && (
            <input
              className={inputProps.className}
              placeholder={inputProps.placeholder}
              disabled={inputProps.disabled}
              value={internalValue}
              // Masked inputs are driven by IMask ('accept' handler); a no-op onChange keeps React from
              // warning about a controlled value without onChange.
              onChange={!mask ? _onChange : noopChange}
              onBlur={_onBlur}
              autoComplete={'off'}
              ref={ref as any}
              type={mask === 'ru-phone' ? 'tel' : internalType}
              style={phoneMaskEmpty ? { color: '#636a70' } : undefined}
              name={name}
              aria-invalid={isInvalid ? true : undefined}
              aria-describedby={errorText ? errorId : undefined}
            />
          )}
          {!input && tag === 'textarea' && (
            <textarea
              className={textareaProps.className}
              placeholder={textareaProps.placeholder}
              disabled={textareaProps.disabled}
              onChange={(e) => onChange?.(e.target.value)}
              onBlur={(e) => onBlurProp?.(e)}
              ref={ref as any}
              name={name}
              aria-invalid={isInvalid ? true : undefined}
              aria-describedby={errorText ? errorId : undefined}
              rows={rows}
              value={value ?? ''}
            />
          )}
          {suffix && <span className={suffixProps.className}>{suffix}</span>}
        </div>
        {endAdornmentFinal &&
          (type === 'password' && !disabled ? (
            <button
              type='button'
              className={endAdornmentProps.className}
              onClick={_endAdornmentOnClick}
              aria-label={internalType === 'password' ? 'Показать пароль' : 'Скрыть пароль'}
              tabIndex={0}
            >
              {endAdornmentFinal}
            </button>
          ) : (
            <div className={endAdornmentProps.className} onClick={_endAdornmentOnClick}>
              {endAdornmentFinal}
            </div>
          ))}
      </div>
      {isString(helper) ? (
        <div className={helperTextProps.className}>{helper}</div>
      ) : (
        helper
      )}
      <TextFieldMessage id={errorId} isInvalid={isInvalid} />
    </label>
  );
};

export const TextField = React.memo(React.forwardRef(TextFieldInternal));
